import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import WeekHoursEditor from "@/components/settings/WeekHoursEditor";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const field = "rounded-xl bg-white border-[#103080]/20 h-11";

export default function EditMemberDialog({ member, isSelf, onClose, onSaved }) {
  const [f, setF] = useState(null);

  useEffect(() => {
    if (member) setF({ name: member.name, email: member.email, works_number: member.works_number || "", role: member.role, active: member.active, settings: member.settings });
  }, [member]);

  if (!member || !f) return null;
  const s = f.settings;

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/team/${member.id}`, { ...f, ...(isSelf ? { email: undefined, role: undefined, active: undefined } : {}) });
      toast.success("Team member updated");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl bg-[#FDFBF7] border-[#103080]/10 sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="edit-member-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-brand text-2xl text-[#103080]">Edit {member.name}</DialogTitle>
          <DialogDescription>Name, sign-in email, access and contracted working hours.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} data-testid="edit-member-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Payroll works number</Label>
              <Input value={f.works_number} onChange={(e) => setF({ ...f, works_number: e.target.value })} className={field} placeholder="For BrightPay" data-testid="edit-member-works-number" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Sign-in email</Label>
            <Input type="email" required disabled={isSelf} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} data-testid="edit-member-email" />
          </div>
          {!isSelf && (
            <div className="grid grid-cols-2 gap-3 items-end">
              <div className="space-y-1.5">
                <Label>Access</Label>
                <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                  <SelectTrigger className={field} data-testid="edit-member-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Team member</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#103080] h-11">
                <Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="edit-member-active" />
                {f.active ? "Can sign in" : "Paused"}
              </label>
            </div>
          )}
          <div className="space-y-2">
            <Label>Usual working week</Label>
            <WeekHoursEditor value={s} onChange={(v) => setF({ ...f, settings: v })} testPrefix="edit-member" />
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" data-testid="edit-member-save"
              className="rounded-full bg-[#103080] text-white font-medium px-7 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200">Save changes</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
